import React from "react";
import { FiSun, FiMoon } from "react-icons/fi";

/* ── Icon components ──────────────────────────────────────────── */
function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M21 21L16.65 16.65M18 10.5C18 14.6421 14.6421 18 10.5 18C6.35786 18 3 14.6421 3 10.5C3 6.35786 6.35786 3 10.5 3C14.6421 3 18 6.35786 18 10.5Z"
        stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

function MessageIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M8 18.5H7C5.34315 18.5 4 17.1569 4 15.5V8.5C4 6.84315 5.34315 5.5 7 5.5H17C18.6569 5.5 20 6.84315 20 8.5V15.5C20 17.1569 18.6569 18.5 17 18.5H12.5L8.5 21V18.5H8Z"
        stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

function CartIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 4H5.2L7 14H18.3L20.2 7H8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="9" cy="19" r="1.5" fill="currentColor" />
      <circle cx="17" cy="19" r="1.5" fill="currentColor" />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 7H20M4 12H20M4 17H20" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    </svg>
  );
}

/* ── Category config — keys match App.jsx filter logic ───────── */
const NAV_CATEGORIES = [
  { label: "TẤT CẢ",    key: "all"       },
  { label: "ÁO",        key: "shirt_all" },
  { label: "QUẦN",      key: "pants_all" },
  { label: "LINH KIỆN", key: "acc_all"   },
];

/* ── Header ───────────────────────────────────────────────────── */
export default function Header({
  isDarkMode        = false,
  isHeaderVisible   = true,
  searchQuery       = "",
  setSearchQuery,
  cartItemCount     = 0,
  currentCategory   = "all",
  navigateTo,
  setIsCartOpen,
  setIsUnifiedMenuOpen,
  setIsHelpOpen,
  handleThemeToggle,
}) {
  /* Logo: go home */
  const handleLogoClick = (e) => {
    e.preventDefault();
    navigateTo?.("home", "all");
  };

  /* Search: controlled input + submit on Enter */
  const handleSearchChange = (e) => setSearchQuery?.(e.target.value);
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    navigateTo?.("shop", "all");
  };

  /* Category tabs */
  const handleCategoryClick = (key) => navigateTo?.("shop", key);

  /* Action buttons */
  const handleCartClick    = () => setIsCartOpen?.(true);
  const handleMessageClick = () => setIsHelpOpen?.(true);
  const handleMenuClick    = () => setIsUnifiedMenuOpen?.(true);

  const dark = isDarkMode;

  return (
    <>
      <header
        className="trimi-header"
        style={{
          transform: isHeaderVisible ? "translateY(0)" : "translateY(-100%)",
          background: dark ? "#0f1824" : "#ffffff",
          borderBottomColor: dark ? "rgba(255,255,255,0.07)" : "#eceff3",
          color: dark ? "#f1f5fc" : "#111827",
        }}
      >
        <div className="trimi-header__inner">
          {/* ── TOP ROW: Logo · Search · Actions ── */}
          <div className="trimi-header__top">
            {/* Logo */}
            <a
              href="/"
              className="trimi-header__logo"
              aria-label="TRIMI — Trang chủ"
              onClick={handleLogoClick}
            >
              <img src="/apple-touch-icon.png" alt="TRIMI" />
            </a>

            {/* Search */}
            <form
              className="trimi-header__search"
              onSubmit={handleSearchSubmit}
              role="search"
            >
              <input
                type="text"
                placeholder="Tìm kiếm sản phẩm..."
                value={searchQuery}
                onChange={handleSearchChange}
                aria-label="Tìm kiếm"
                style={{
                  background: dark ? "#16202e" : "#f3f6fa",
                  color:      dark ? "#f1f5fc" : "#1f2937",
                  borderColor: dark ? "rgba(255,255,255,0.10)" : "#e8edf4",
                }}
              />
              <button type="submit" aria-label="Tìm kiếm">
                <SearchIcon />
              </button>
            </form>

            {/* Action icons */}
            <div className="trimi-header__actions">
              {/* Dark / Light toggle */}
              <button
                type="button"
                className="trimi-icon-btn"
                aria-label={dark ? "Chuyển sang chế độ sáng" : "Chuyển sang chế độ tối"}
                onClick={handleThemeToggle}
                style={{ color: dark ? "#f1f5fc" : "#111827" }}
              >
                {dark ? <FiSun size={22} /> : <FiMoon size={22} />}
              </button>

              {/* Message / Chat */}
              <button
                type="button"
                className="trimi-icon-btn"
                aria-label="Mở chat tư vấn"
                onClick={handleMessageClick}
                style={{ color: dark ? "#f1f5fc" : "#111827" }}
              >
                <MessageIcon />
              </button>

              {/* Cart */}
              <button
                id="header-cart-icon"
                type="button"
                className="trimi-icon-btn trimi-cart-btn"
                aria-label={`Giỏ hàng (${cartItemCount} sản phẩm)`}
                onClick={handleCartClick}
                style={{ color: dark ? "#f1f5fc" : "#111827" }}
              >
                <CartIcon />
                {cartItemCount > 0 && (
                  <span className="trimi-cart-badge">{cartItemCount}</span>
                )}
              </button>

              {/* Hamburger menu */}
              <button
                type="button"
                className="trimi-icon-btn"
                aria-label="Mở menu"
                onClick={handleMenuClick}
                style={{ color: dark ? "#f1f5fc" : "#111827" }}
              >
                <MenuIcon />
              </button>
            </div>
          </div>

          {/* ── CATEGORY NAV ── */}
          <nav className="trimi-header__nav" aria-label="Danh mục sản phẩm">
            {NAV_CATEGORIES.map(({ label, key }) => {
              const isActive = currentCategory === key
                || (key === "shirt_all" && currentCategory?.startsWith("shirt"))
                || (key === "pants_all" && currentCategory?.startsWith("pants"))
                || (key === "acc_all"   && currentCategory?.startsWith("acc"));
              return (
                <button
                  key={key}
                  type="button"
                  className={`trimi-nav-btn ${isActive ? "active" : ""}`}
                  onClick={() => handleCategoryClick(key)}
                  style={{
                    color: isActive
                      ? (dark ? "#7cc4ff" : "#111827")
                      : (dark ? "#8aa0be" : "#7d8795"),
                  }}
                >
                  {label}
                  {isActive && <span className="trimi-nav-underline" />}
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      <style>{`
        .trimi-header {
          width: 100%;
          border-bottom: 1px solid;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 9000;
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
                      background 0.25s ease,
                      border-color 0.25s ease;
        }

        .trimi-header__inner {
          max-width: 1680px;
          margin: 0 auto;
          padding: 0 32px;
        }

        .trimi-header__top {
          min-height: 70px;
          display: grid;
          grid-template-columns: 160px 1fr 220px;
          align-items: center;
          gap: 20px;
          border-bottom: 1px solid inherit;
        }

        .trimi-header__logo {
          display: inline-flex;
          align-items: center;
          justify-content: flex-start;
          text-decoration: none;
          flex-shrink: 0;
        }

        .trimi-header__logo img {
          width: 90px;
          height: auto;
          display: block;
          object-fit: contain;
          transition: opacity 0.2s ease;
        }
        .trimi-header__logo:hover img { opacity: 0.8; }

        .trimi-header__search {
          width: 100%;
          position: relative;
        }

        .trimi-header__search input {
          width: 100%;
          height: 46px;
          border: 1px solid;
          outline: none;
          border-radius: 999px;
          padding: 0 52px 0 20px;
          font-size: 15px;
          font-weight: 500;
          transition: box-shadow 0.2s ease, border-color 0.2s ease;
        }
        .trimi-header__search input::placeholder { color: #9aa4b2; }
        .trimi-header__search input:focus {
          box-shadow: 0 0 0 3px rgba(47, 128, 237, 0.15);
          border-color: #2f80ed !important;
        }

        .trimi-header__search button {
          position: absolute;
          top: 50%;
          right: 14px;
          transform: translateY(-50%);
          width: 28px;
          height: 28px;
          border: 0;
          background: transparent;
          color: #9aa4b2;
          padding: 0;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: color 0.2s ease;
        }
        .trimi-header__search button:hover { color: #2f80ed; }
        .trimi-header__search button svg { width: 20px; height: 20px; }

        .trimi-header__actions {
          justify-self: end;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .trimi-icon-btn {
          position: relative;
          width: 38px;
          height: 38px;
          border: 0;
          background: transparent;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          border-radius: 10px;
          transition: background 0.18s ease, transform 0.18s ease, color 0.18s ease;
        }
        .trimi-icon-btn:hover {
          background: rgba(47, 128, 237, 0.10);
          transform: translateY(-1px);
        }
        .trimi-icon-btn:active { transform: translateY(0); }
        .trimi-icon-btn svg { width: 22px; height: 22px; }
        .trimi-cart-btn svg { width: 24px; height: 24px; }

        .trimi-cart-badge {
          position: absolute;
          top: 2px;
          right: 2px;
          min-width: 17px;
          height: 17px;
          border-radius: 999px;
          background: #2f80ed;
          color: #fff;
          font-size: 10px;
          font-weight: 800;
          line-height: 17px;
          text-align: center;
          padding: 0 4px;
          box-shadow: 0 0 0 2px #fff;
          pointer-events: none;
        }

        .trimi-header__nav {
          min-height: 46px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 48px;
          overflow-x: auto;
          scrollbar-width: none;
        }
        .trimi-header__nav::-webkit-scrollbar { display: none; }

        .trimi-nav-btn {
          position: relative;
          border: 0;
          background: transparent;
          padding: 0 2px 8px;
          font-size: 13px;
          font-weight: 800;
          letter-spacing: 0.16em;
          cursor: pointer;
          transition: color 0.2s ease, transform 0.2s ease;
          white-space: nowrap;
        }
        .trimi-nav-btn:hover { transform: translateY(-1px); }

        .trimi-nav-underline {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: #2f80ed;
          border-radius: 999px;
        }

        /* ── Responsive ── */
        @media (max-width: 1024px) {
          .trimi-header__top {
            grid-template-columns: auto 1fr auto;
            min-height: 60px;
            gap: 12px;
          }
          .trimi-header__nav { gap: 28px; }
        }

        @media (max-width: 640px) {
          .trimi-header__inner { padding: 0 14px; }
          .trimi-header__top {
            grid-template-columns: auto 1fr auto;
            min-height: 56px;
          }
          .trimi-header__logo img { width: 72px; }
          .trimi-header__search input { height: 40px; font-size: 14px; }
          .trimi-header__actions { gap: 2px; }
          .trimi-icon-btn { width: 34px; height: 34px; }
          .trimi-icon-btn svg { width: 20px; height: 20px; }
          .trimi-header__nav { gap: 18px; min-height: 40px; }
          .trimi-nav-btn { font-size: 12px; letter-spacing: 0.10em; }
        }
      `}</style>
    </>
  );
}